import type {
    Iso8601DateTime,
    Uuid,
} from "@vesta/common-type-definitions/typeDefinitions";
import {z as zod} from "zod";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

/** Empty assignments are valid configuration and must grant no scoped access. */
export const geographyAssignmentValueValidator = uuidZodValidator.array().refine(
    (ids) => new Set(ids).size === ids.length,
    {message: "Geography assignments must not contain duplicate IDs"},
);

export enum UserAdditionalColumnType {
    geographyCountries = "efd45ab3-19a0-4a07-bb4f-b8828600e147",
    geographyIndiaCities = "63e90863-6cf4-4aa6-b7d8-3ab5fc8aa743",
    geographyIndiaPincodes = "d826e34c-2724-4c50-9b1e-e535624b7021",
    geographyIndiaStates = "7f21de8d-2d51-43c3-aea2-673f1bea037b",
    geographyIndiaZones = "4d3863fe-6dd9-4538-8ba4-957bcfce0902",
    geographyLatitude = "1fb6fe0d-6899-4587-a9d5-cd551a823ab8",
    geographyLongitude = "6dc339df-9910-4552-8830-02cb511d9425",
    image = "8c9c33b6-85c4-4a46-aab3-654404443c1e",
    json = "fc6bbe57-3b75-4b04-b06f-5f7ba2ac2d10",
    number = "1aa88529-bffa-4db5-9a23-fd3dfe919764",
    ou = "d9088984-2b17-4093-92b4-513373334136",
    ous = "4b5c1050-03e2-4f43-b3f2-6865e148ee08",
    // Retired after the select-options-to-UDO migration: select = "8857d40e-9a3e-415c-befc-626ce3f3e8a3",
    // TODO: Either this or text needs to be deprecated
    string = "c9502e96-57c4-4231-aa97-2ce6c68c7861",
    // TODO: Either this or string needs to be deprecated
    text = "2df71b5d-35e5-43c2-871e-e59cb8a2a2e3",
    timestamp = "6cc57077-232b-4a65-827b-70fd80eca9c7",
    user = "c563de89-f344-4cc8-afa5-965562edbece",
    userDefinedObject = "a204fd5a-3078-4f7e-a845-1dfb09363bb7",
    userDefinedObjects = "a06f661b-51b1-42ad-b558-301adc3bbd57",
    userTemplate = "6c00feb8-9a48-4d01-8821-25a6e4cb6359",
    uuid = "823eacbd-0a25-4311-b7d1-fc7fa2b515d0",
}

export type UserAdditionalColumnConfigurationMap = {
    [UserAdditionalColumnType.geographyCountries]: { required: boolean };
    [UserAdditionalColumnType.geographyIndiaCities]: { required: boolean };
    [UserAdditionalColumnType.geographyIndiaPincodes]: { required: boolean };
    [UserAdditionalColumnType.geographyIndiaStates]: { required: boolean };
    [UserAdditionalColumnType.geographyIndiaZones]: { required: boolean };
    [UserAdditionalColumnType.geographyLatitude]: { required: boolean };
    [UserAdditionalColumnType.geographyLongitude]: { required: boolean };
    [UserAdditionalColumnType.image]: { required: boolean };
    [UserAdditionalColumnType.json]: { ouTemplateId?: Uuid; required: boolean };
    [UserAdditionalColumnType.number]: { required: boolean };
    [UserAdditionalColumnType.ou]: { required: boolean };
    [UserAdditionalColumnType.ous]: { ouTemplateId: Uuid; required: boolean };
    [UserAdditionalColumnType.string]: { required: boolean };
    [UserAdditionalColumnType.text]: { required: boolean };
    [UserAdditionalColumnType.user]: { required: boolean };
    [UserAdditionalColumnType.userDefinedObject]: {
        required: boolean;
        userDefinedObjectTemplateId: Uuid;
    };
    [UserAdditionalColumnType.userDefinedObjects]: {
        required: boolean;
        userDefinedObjectTemplateId: Uuid;
    };
    [UserAdditionalColumnType.userTemplate]: { required: boolean };
    [UserAdditionalColumnType.uuid]: { required: boolean };
    [UserAdditionalColumnType.timestamp]: { required: boolean };
};

export type UserAdditionalColumnValueTypeMap = {
    [UserAdditionalColumnType.geographyCountries]: Array<Uuid>;
    [UserAdditionalColumnType.geographyIndiaCities]: Array<Uuid>;
    [UserAdditionalColumnType.geographyIndiaPincodes]: Array<Uuid>;
    [UserAdditionalColumnType.geographyIndiaStates]: Array<Uuid>;
    [UserAdditionalColumnType.geographyIndiaZones]: Array<Uuid>;
    [UserAdditionalColumnType.geographyLatitude]: number | null;
    [UserAdditionalColumnType.geographyLongitude]: number | null;
    [UserAdditionalColumnType.image]: string;
    [UserAdditionalColumnType.json]: string;
    [UserAdditionalColumnType.number]: number;
    [UserAdditionalColumnType.ou]: Uuid | null;
    [UserAdditionalColumnType.ous]: Array<Uuid>;
    [UserAdditionalColumnType.string]: string;
    [UserAdditionalColumnType.text]: string;
    [UserAdditionalColumnType.timestamp]: Iso8601DateTime;
    [UserAdditionalColumnType.user]: Uuid | null;
    [UserAdditionalColumnType.userDefinedObject]: Uuid | null;
    [UserAdditionalColumnType.userDefinedObjects]: Array<Uuid>;
    [UserAdditionalColumnType.userTemplate]: Uuid;
    [UserAdditionalColumnType.uuid]: Uuid;
};

export type UserTemplateAdditionalColumn = {
    displayName: string;
    id: Uuid;
} & {
    [K in UserAdditionalColumnType]: {
        configuration: UserAdditionalColumnConfigurationMap[K];
        type: K;
    };
}[UserAdditionalColumnType];

const userAdditionalColumnBaseValidator = zod.object({
    displayName: zod.string(),
    id: uuidZodValidator,
    resolver: zod.record(zod.string(), zod.unknown()).optional(),
});

export const userTemplateAdditionalColumnsZodValidator: zod.ZodType<UserTemplateAdditionalColumn> = zod.discriminatedUnion("type", [
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.strictObject({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.geographyCountries),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.strictObject({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.geographyIndiaCities),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.strictObject({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.geographyIndiaPincodes),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.strictObject({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.geographyIndiaStates),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.strictObject({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.geographyIndiaZones),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.strictObject({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.geographyLatitude),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.strictObject({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.geographyLongitude),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.image),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            ouTemplateId: uuidZodValidator.optional(),
            required: zod.boolean(),
        }),
        type: zod.literal(UserAdditionalColumnType.json),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.number),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.ou),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            ouTemplateId: uuidZodValidator,
            required: zod.boolean(),
        }),
        type: zod.literal(UserAdditionalColumnType.ous),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.string),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.text),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.user),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            required: zod.boolean(),
            userDefinedObjectTemplateId: uuidZodValidator,
        }),
        type: zod.literal(UserAdditionalColumnType.userDefinedObject),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            required: zod.boolean(),
            userDefinedObjectTemplateId: uuidZodValidator,
        }),
        type: zod.literal(UserAdditionalColumnType.userDefinedObjects),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.userTemplate),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.uuid),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.timestamp),
    }),
]);

// function getSchemaPropertyTypeFromUserAdditionalColumnType(additionalColumnType: UserAdditionalColumnType): SchemaPropertyType {
//     switch (additionalColumnType) {
//         // TODO: Revisit this, shouldn't it be uuid ??
//         case UserAdditionalColumnType.image: {
//             return SchemaPropertyType.string;
//         }
//         case UserAdditionalColumnType.json: {
//             return SchemaPropertyType.json;
//         }
//         case UserAdditionalColumnType.number: {
//             return SchemaPropertyType.number;
//         }
//         case UserAdditionalColumnType.ou: {
//             return SchemaPropertyType.uuid;
//         }
//         // TODO: Verify this, shouldn't it be uuid[] ??
//         case UserAdditionalColumnType.ous: {
//             return SchemaPropertyType.json;
//         }
//         case UserAdditionalColumnType.select: {
//             return SchemaPropertyType.uuid;
//         }
//         case UserAdditionalColumnType.string: {
//             return SchemaPropertyType.string;
//         }
//         case UserAdditionalColumnType.text: {
//             return SchemaPropertyType.string;
//         }
//         case UserAdditionalColumnType.user: {
//             return SchemaPropertyType.uuid;
//         }
//         case UserAdditionalColumnType.userTemplate: {
//             return SchemaPropertyType.uuid;
//         }
//         case UserAdditionalColumnType.uuid: {
//             return SchemaPropertyType.uuid;
//         }
//         case UserAdditionalColumnType.timestamp: {
//             return SchemaPropertyType.timestamp;
//         }
//         default: {
//             throw new Error(`Unsupported additional column type: ${String(additionalColumnType)}`);
//         }
//     }
// }

// export function getSchemaPropertiesFromUserTemplateAdditionalColumns(
//     additionalColumns: Array<UserTemplateAdditionalColumn>,
//     //
// ): Record<string, SchemaProperty>
//  {
//     const properties: Record<string, SchemaProperty> = {};
//     for (const additionalColumn of additionalColumns) {
//         const columnNameInSnakeCase = toSnakeCase(additionalColumn.displayName);
//         if (properties[columnNameInSnakeCase] !== undefined) {
//             throw new Error(`Duplicate additional column display name: ${additionalColumn.displayName}`);
//         }
//         properties[columnNameInSnakeCase] = {
//             displayName: additionalColumn.displayName,
//             id: additionalColumn.id,
//             internalName: toCamelCase(additionalColumn.displayName),
//             required: additionalColumn.configuration.required,
//             type: getSchemaPropertyTypeFromUserAdditionalColumnType(additionalColumn.type),
//         };
//     }
//     return properties;
// }
